import type { DeckStudyCard } from "@-/dev.improve.haniverse.lib.cards";
import {
  cardContentDrillType,
  type CardContentKind,
  resolveUserCardContentKind,
} from "@-/dev.improve.haniverse.lib.cards.card-content";
import type { DrillType } from "@-/dev.improve.haniverse.lib.drill.entities";

/**
 * Which drill a deck tile represents. One-to-one with `DrillType["kind"]` — a tile IS a drill, and
 * `deckStudyCardTileType` derives it through `cardContentDrillType` so the two cannot drift.
 */
export type DeckStudyCardTileType = DrillType["kind"];

/**
 * A pronunciation card's own tone-number reading (干 gan4 or 干 gan1), which its tile shows and is
 * tone-coloured by; `null` for every other card, as `StageCard.pinyin` on the Stats page.
 */
export function deckStudyCardPinyin(studyCard: DeckStudyCard): string | null {
  return studyCard.userGraphemePronunciationCard?.pinyin ?? null;
}

/** Which card-content kind a deck row is, from whichever overlay it carries. */
export function deckStudyCardContentKind(studyCard: DeckStudyCard): CardContentKind | null {
  return resolveUserCardContentKind({
    userGraphemeMeaningCard: studyCard.userGraphemeMeaningCard,
    userGraphemePronunciationCard: studyCard.userGraphemePronunciationCard,
    userWordSentenceCard: studyCard.userWordSentenceCard,
  });
}

/**
 * Which drill this card is, for the tile.
 *
 * Goes through the card-content registry rather than inferring from overlay PRESENCE: all three
 * word drills share one overlay table, so presence alone would label a word's three cards
 * identically and the deck would show three indistinguishable tiles for one word.
 *
 * Returns `undefined` when no overlay is present, which callers already skip.
 */
export function deckStudyCardTileType(studyCard: DeckStudyCard): DeckStudyCardTileType | undefined {
  const contentKind = deckStudyCardContentKind(studyCard);
  // A quiz is not drilled, so it has no drill tile.
  if (contentKind === null || contentKind === "quiz") {
    return undefined;
  }
  const drillType = cardContentDrillType(
    // Only `kind` decides the tile type; the payload fields are irrelevant here, so a word card's
    // text/reading are stubbed rather than re-read.
    contentKind === "grapheme-meaning" || contentKind === "grapheme-pronunciation"
      ? { grapheme: "", kind: contentKind, pinyin: "" }
      : { kind: contentKind, pinyin: "", word: "" },
  );
  return drillType.kind;
}
